import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Alert,
  Typography,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { Event, EventStatus, EventType, RoomType } from '../../services/Types';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import SchedulerCustom from './SchedulerCustom';
import SchedulerOpen from './SchedulerOpen';
import { SelectEventType } from '../../components/SelectEventType/SelectEventType';
import { Footer } from './Footer';
import { gridColumn, verticalBorders } from '../../global';
import { useEvent } from '../../hooks/useEvent';
import { useToast } from '../../hooks/useToast';
import { useRoomScheduling } from '../../hooks/useRoomScheduling';
import { ConfirmationModal } from './ConfirmationModal';
import { parseDateParam, formatDateParam, updateSearchParams } from '../../utils/urlParams';

// Add TabPanel interface and component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`time-view-tabpanel-${index}`}
      aria-labelledby={`time-view-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `time-view-tab-${index}`,
    'aria-controls': `time-view-tabpanel-${index}`,
  };
}

export default function Scheduler() {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Form state
  const { createDbEvent, emptyEventData, errorUseEvent } = useEvent();
  const { showToast, renderToast } = useToast();
  
  // Initialize state from URL params
  const initialEventType = searchParams.get('eventType') as EventType | null;
  const initialDate = parseDateParam(searchParams.get('date'), new Date());
  const initialRoom = searchParams.get('room') as RoomType | null;
  const initialTimeMode = searchParams.get('timeMode');
  const initialTimeModeTab = initialTimeMode === 'custom' ? 1 : 0;
  
  const [event, setEvent] = useState<Event>(() => {
    const emptyEvent = emptyEventData();
    if (initialEventType) {
      emptyEvent.eventType = initialEventType;
    }
    return emptyEvent;
  });

  const [startTime, setStartTime] = useState(0);
  const [endTime, setEndTime] = useState(0);
  const [selectedDate, setSelectedDate] = useState<Date | null>(initialDate);
  const [disabled, setDisabled] = useState(true);
  const [roomSelected, setRoomSelected] = useState<RoomType | null>(initialRoom);

  // Time selection state
  const [timeModeTab, setTimeModeTab] = useState(initialTimeModeTab);

  // Confirmation modal state
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  // Update URL params when state changes
  const updateURLParams = useCallback((updates: Record<string, string | null | undefined>) => {
    const newParams = updateSearchParams(searchParams, updates);
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Room scheduling hook: note all parameters are auto updated by the hook
  const { checkMultipleAvailability, createRoomBooking, isBooking } = useRoomScheduling(
    event,
    startTime,
    endTime,
    selectedDate,
    true // adminBooking: true for admin operations
  );

  useEffect(() => {
    if (event.eventType && event.title && endTime && (startTime > 0 || endTime === 1439)) {
      setDisabled(false);
    } else {
      setDisabled(true);
    }
  }, [event, startTime, endTime])

  const handleSave = () => {
    if (!roomSelected || !selectedDate) {
      showToast(true, 'Room selection or date is missing', 'error');
      return;
    }

    setShowConfirmationModal(true);
  };

  const handleConfirmSave = async () => {
    setShowConfirmationModal(false);

    const { availableRoomIds, error: schedulingError } = await checkMultipleAvailability(roomSelected!);
    
    if (schedulingError) {
      showToast(true, schedulingError, 'error');
      return;
    }

    // Create the event
    const newEvent = {
      ...event,
      roomBooking: [], // Bookings are handled by createRoomBooking
      overHundredParticipants: availableRoomIds!.some((_, index) => {
        // Determine if any room is for large audience based on room selection
        if (roomSelected === RoomType.largeRoom) return true;
        if (roomSelected === RoomType.allRooms) return index === 2; // Third room is large
        return false;
      })
    };

    // TODO: remove this once we have a proper way to get the approvers
    if (newEvent.levelOneApprover?.id) {
      newEvent.levelOneApproverId = newEvent.levelOneApprover.id
    }
    if (newEvent.programOffice?.id) {
      newEvent.programOfficeId = newEvent.programOffice.id
    }

    if (newEvent.eventType === EventType.BLOCKED) {
      newEvent.status = EventStatus.BLOCKED;
    }
    setEvent(newEvent);
    
    createDbEvent(newEvent, true).then((eventId) => {
      if (eventId) {
        newEvent.id = eventId;
        createRoomBooking(eventId, availableRoomIds!).then((bookingError) => {
          if (!bookingError) {
            showToast(true, 'Event saved successfully', 'success');
          } else {
            showToast(true, bookingError, 'error');
          }
        });
      } else {
        showToast(true, 'Error creating event', 'error');
      }
    });
  };

  const handleCancelSave = () => {
    setShowConfirmationModal(false);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTimeModeTab(newValue);
    // Reset time values when switching to Open Times tab
    if (newValue === 0) {
      setStartTime(0);
      setEndTime(0);
    }
    // Update URL
    updateURLParams({ timeMode: newValue === 0 ? 'open' : 'custom' });
  };

  // Update URL when date changes
  const handleDateChange = useCallback((newValue: Date | null) => {
    setSelectedDate(newValue);
    updateURLParams({ date: newValue ? formatDateParam(newValue) : null });
  }, [updateURLParams]);

  // Update URL when event type changes
  const handleEventTypeChange = useCallback((value: string) => {
    const eventType = value as EventType;
    setEvent(prev => ({ ...prev, eventType }));
    updateURLParams({ eventType: value || null });
  }, [updateURLParams]);

  // Update URL when room changes
  const handleRoomChange = useCallback((value: RoomType) => {
    setRoomSelected(value);
    updateURLParams({ room: value || null });
  }, [updateURLParams]);

  return (
    <Grid container spacing={0}>
      {/* Left Column - Form Fields */}
      <Grid item xs={12} md={4} sx={{ ...gridColumn, pl: 0 }}>
        <TextField
            fullWidth
            label="Event Title"
            value={event.title}
            onChange={(e) => setEvent({...event, title: e.target.value})}
        />
        <FormControl fullWidth>
          <SelectEventType
            value={event.eventType}
            onChange={handleEventTypeChange}
          />
          { event.eventType === EventType.WEBINAR && (
            <Typography variant="body2" color="text.secondary">
              Note: Webinar events require a 30 min buffer so please select start time and end time appropriately.
            </Typography>
          )}
        </FormControl>

        <FormControl fullWidth>
          <InputLabel>Room</InputLabel>
          <Select
            value={roomSelected || ''}
            label="Room"
            onChange={(e) => {
              handleRoomChange(e.target.value as RoomType);
            }}
          >
            {Object.entries(RoomType).map(([key, value]) => (
              <MenuItem key={key} value={value}>{value}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {/* Middle Column - Calendar */}
      <Grid item xs={12} md={4} sx={{ ...verticalBorders, ...gridColumn }}>
        <StaticDatePicker
          label="Select Date"
          value={selectedDate}
          onChange={handleDateChange}
          renderInput={(params) => <TextField {...params} fullWidth />}
        />
      </Grid>

      {/* Right Column - Time Selection */}
      <Grid item xs={12} md={4} sx={{ ...gridColumn, pr: 0 }}>
        <Tabs 
          value={timeModeTab} 
          onChange={handleTabChange}
          aria-label="Time view mode"
        >
          <Tab label="Open Times" {...a11yProps(0)} sx={{ minWidth: "49%" }} />
          <Tab label="Custom Times" {...a11yProps(1)} sx={{ minWidth: "49%" }} />
        </Tabs>

        <TabPanel value={timeModeTab} index={0}>
          <SchedulerOpen
            selectedDate={selectedDate}
            eventType={event.eventType as EventType}
            startTime={startTime}
            endTime={endTime}
            setStartTime={setStartTime}
            setEndTime={setEndTime}
          />
        </TabPanel>
        <TabPanel value={timeModeTab} index={1}>
          <SchedulerCustom
            startTime={startTime}
            endTime={endTime}
            setStartTime={setStartTime}
            setEndTime={setEndTime}
          />
        </TabPanel>
        {errorUseEvent && (<Alert severity="error">{errorUseEvent}</Alert>)}
      </Grid>
      <Grid item xs={12}>
        <Footer
          onSave={handleSave}
          onCancel={() => {window.location.reload();}}
          disabled={disabled || isBooking}
        />
      </Grid>
      <ConfirmationModal
        open={showConfirmationModal}
        onClose={handleCancelSave}
        onConfirm={handleConfirmSave}
      />
      {renderToast()}
    </Grid>
  );
} 